import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Building2, 
  GraduationCap,
  Briefcase,
  Receipt,
  ChevronDown,
  LogOut,
  User,
  History,
  MapPin,
  Tent,
  MonitorPlay,
  Inbox,
  Layers,
  LayoutTemplate,
  CalendarRange,
  ListTree,
  Rocket,
  BookOpen,
  ListChecks
} from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import { serviceRequestsAPI, purchaseOrdersAPI } from '../../services/api';

const myTasksItems = [
  { path: '/my-tasks', label: 'My Open Tasks', icon: ListChecks },
];

// Operations — visible to ALL PMs (not admin-only). Create actions stay role-gated inside the pages.
const operationsItems = [
  { path: '/service-requests', label: 'Service Requests', icon: Inbox, badgeKey: 'sr_new' },
  { path: '/purchase-orders', label: 'Purchase Orders', icon: Receipt, badgeKey: 'po_recent' },
];

// Training Delivery — org-wide delivery dashboard + every delivery type and supporting roster
const trainingDeliveryItems = [
  { path: '/dashboard', label: 'Training Delivery Dashboard', icon: LayoutDashboard, adminOnly: true },
  { path: '/training-projects', label: 'Private Deliveries', icon: FileText },
  { path: '/partner-conferences', label: 'Partner Conferences', icon: MapPin },
  { path: '/regional-in-person', label: 'Regional In-Person', icon: Tent },
  { path: '/pprw', label: '1:Many PPRW', icon: MonitorPlay },
  { path: '/courses', label: 'Courses', icon: GraduationCap },
  { path: '/partners', label: 'Partners', icon: Building2 },
  { path: '/trainers', label: 'Trainers', icon: Briefcase },
];

// Content Development — new vertical: content build/refresh lifecycle for Microsoft partner content
const contentDevItems = [
  { path: '/content-development', label: 'Content Dashboard', icon: LayoutTemplate },
  { path: '/content-items', label: 'ContentDev Tracker', icon: Layers },
  { path: '/workback-template', label: 'Workback Templates', icon: CalendarRange },
];

// Platform (LevelUp++) — self-paced learning platform business: Projects & Tasks tracker
const platformItems = [
  { path: '/platform', label: 'Platform Tracker', icon: Rocket },
  { path: '/platform/kb', label: 'Knowledge Base', icon: BookOpen },
];

const adminItems = [
  { path: '/users', label: 'User Management', icon: Users },
  { path: '/audit-log', label: 'Audit Log', icon: History },
  { path: '/taxonomy', label: 'Taxonomy', icon: ListTree, superAdminOnly: true },
];

// Left-nav bucket order (titles shown as section headers).
const navSections = [
  { key: 'my-tasks', title: 'My Tasks', items: myTasksItems },
  { key: 'operations', title: 'Operations', items: operationsItems },
  { key: 'training-delivery', title: 'Training Delivery', items: trainingDeliveryItems },
  { key: 'content-development', title: 'Content Development', items: contentDevItems },
  { key: 'platform', title: 'Platform (LevelUp++)', items: platformItems },
  { key: 'administration', title: 'Administration', items: adminItems, adminOnly: true },
];

export const Sidebar = () => {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [srStats, setSrStats] = useState({ new_count: 0 });
  const [poStats, setPoStats] = useState({ recent_count: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [sr, po] = await Promise.all([
          serviceRequestsAPI.getStats().catch(() => null),
          purchaseOrdersAPI.getRecentCount().catch(() => null),
        ]);
        if (sr) setSrStats(sr);
        if (po) setPoStats(po);
      } catch (e) {
        // silent fail — don't break sidebar
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBadge = () => {
    if (isSuperAdmin && isSuperAdmin()) return 'Super Admin';
    if (isAdmin()) return 'Admin';
    return 'Project Manager';
  };

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-secondary flex flex-col z-50" data-testid="main-sidebar">
      {/* Logo */}
      <div className="p-5 border-b border-white/10">
        <div className="inline-flex bg-white/95 rounded-md px-2 py-1 mb-3" data-testid="sidebar-technofocus-logo">
          <img 
            src="/technofocus-logo.png" 
            alt="Technofocus" 
            className="h-3.5 w-auto"
          />
        </div>
        <Link to="/" data-testid="sidebar-logo">
          <img 
            src="/skilltrac-logo-white.svg" 
            alt="Skilltrac" 
            className="h-20 w-auto"
          />
        </Link>
        <p className="text-white/50 text-xs mt-2">Training Delivery Management</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {navSections.map((section) => {
          if (section.adminOnly && !isAdmin()) return null;
          const items = section.items.filter(
            (it) => (!it.adminOnly || isAdmin()) && (!it.superAdminOnly || (isSuperAdmin && isSuperAdmin()))
          );
          if (items.length === 0) return null;
          const badgeCounts = { sr_new: srStats?.new_count || 0, po_recent: poStats?.recent_count || 0 };
          return (
            <div key={section.key}>
              <div className="pt-4 pb-1.5 first:pt-0">
                <p className="px-3 text-[10px] font-semibold text-white/40 uppercase tracking-wider">
                  {section.title}
                </p>
              </div>
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path ||
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path + '/'));
                const badgeCount = item.badgeKey ? (badgeCounts[item.badgeKey] || 0) : 0;
                const badgeTestId = item.badgeKey === 'sr_new'
                  ? 'service-requests-new-badge'
                  : (item.badgeKey === 'po_recent' ? 'purchase-orders-recent-badge' : undefined);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={`
                      flex items-center justify-between gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium
                      transition-all duration-200
                      ${isActive
                        ? 'bg-lime text-lime-foreground'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                      }
                    `}
                  >
                    <span className="flex items-center gap-2.5">
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </span>
                    {badgeCount > 0 && (
                      <span
                        data-testid={badgeTestId}
                        className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
                      >
                        {badgeCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User Section */}
      <div className="p-2.5 border-t border-white/10">
        {/* Notification Bell */}
        <div className="flex justify-end px-1 mb-1.5">
          <NotificationBell />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-white hover:bg-white/10 h-auto py-2 px-2"
              data-testid="user-menu-trigger"
            >
              <div className="flex items-center gap-2 w-full">
                <div className="w-7 h-7 rounded-full bg-lime/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-3.5 h-3.5 text-lime" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <p className="text-[12px] font-medium truncate">{user?.name}</p>
                  <p className="text-[10px] text-white/50">{getRoleBadge()}</p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-white/50 flex-shrink-0" />
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-52">
            <DropdownMenuLabel>
              <div>
                <p className="font-medium text-sm">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="text-destructive focus:text-destructive text-sm"
              data-testid="menu-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
};

export default Sidebar;
